import React, { ReactNode, createContext, useState, useContext, useEffect, useCallback } from 'react';

export interface AccordionContextValue {
  openIds: string[];
  toggle: (menuId: string, keepOpen?: string[]) => void;
  closeAll: () => void;
  isOpen: (id: string) => boolean;
}

const AccordionContext = createContext<AccordionContextValue | null>(null);

export const AccordionProvider = ({
  autoClose = false,
  children,
  initialExpanded = [],
  multiple = false,
}: {
  autoClose?: boolean;
  children: ReactNode;
  initialExpanded?: string | string[];
  multiple?: boolean;
}): JSX.Element => {
  const [openIds, setOpenIds] = useState<string[]>(() =>
    Array.isArray(initialExpanded) ? initialExpanded : [initialExpanded],
  );
  const closeAll = useCallback((): void => setOpenIds([]), [setOpenIds]);
  const isOpen = useCallback((id: string): boolean => openIds.includes(id), [openIds]);

  useEffect(() => {
    const outsideClickHandler = (event: MouseEvent): void => {
      const controls = (event.target as HTMLElement).getAttribute('aria-controls');
      const parentControls = (event.target as HTMLElement).dataset.parentId;
      // Clicking on something other than the open accordion or its open parent accordion
      const isNotAccordion = !controls;
      const isDifferentAccordion = controls && !isOpen(controls);
      const isDifferentParentAccordion = parentControls && !isOpen(parentControls);

      if (isNotAccordion || (isDifferentAccordion && !parentControls) || isDifferentParentAccordion) {
        closeAll();
      }
    };
    if (autoClose) {
      document.body.addEventListener('click', outsideClickHandler, true);
    }
    return (): void => {
      if (autoClose) {
        document.body.removeEventListener('click', outsideClickHandler, true);
      }
    };
  }, [autoClose, isOpen, closeAll]);

  const toggle = (id: string, keepOpen: string[] = []): void => {
    const newOpenIds = multiple ? openIds : [];
    if (isOpen(id)) {
      setOpenIds([...newOpenIds.filter((value) => value !== id), ...keepOpen]);
    } else {
      setOpenIds([...newOpenIds, id, ...keepOpen]);
    }
  };

  return (
    <AccordionContext.Provider value={{ openIds, toggle, closeAll, isOpen }}>{children}</AccordionContext.Provider>
  );
};

export const useAccordionContext = (): AccordionContextValue => {
  const contextValue = useContext(AccordionContext);
  if (!contextValue) {
    throw new Error('useAccordionContext must be used inside an <AccordionProvider>');
  }
  return contextValue;
};
